import { createFileRoute } from "@tanstack/react-router";
import { physicianSchema, safeJsonLd } from "@/lib/structured-data-core";
import { lazy, Suspense, useState } from "react";
import { User, GraduationCap } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { SocialPosts } from "@/components/site/SocialPosts";

const hero = "/images/dr-anish-patel.jpg";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
const Reviews = lazy(() => import("@/components/site/Reviews").then(m => ({ default: m.Reviews })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

export const Route = createFileRoute("/doctors/dr-anish-patel")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/doctors/dr-anish-patel/" }],
    meta: [
      { title: "Dr. Anish Patel — Hair Restoration Physician | RESTORE® Hair" },
      {
        name: "description",
        content:
          "Dr. Anish Patel is a board-certified physician and hair restoration surgeon in Chicago, trained by hair restoration surgeon Dr. James Harris.",
      },
      { property: "og:title", content: "Dr. Anish Patel — Hair Restoration Physician" },
      {
        property: "og:description",
        content:
          "Meet Dr. Anish Patel, a board-certified RESTORE® Hair physician in Chicago dedicated to creating natural-appearing hair restoration results.",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd(
          physicianSchema({
            name: "Dr. Anish Patel",
            jobTitle: "Hair Restoration Physician",
            description: "Dr. Anish Patel is a board-certified physician and hair restoration surgeon based in Chicago, trained extensively by Dr. James Harris.",
            image: `https://restorehair.com${hero}`,
            pageUrl: "https://restorehair.com/doctors/dr-anish-patel/",
            clinic: {
              street: "159 N Sangamon St, Suite 200",
              city: "Chicago",
              state: "IL",
            },
          }),
        ),
      },
    ],
  }),
  component: DrPatelPage,
});

function DrPatelPage() {
  const [tab, setTab] = useState<"bio" | "education">("bio");

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        {/* Hero */}
        <section className="overflow-hidden bg-background">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 pt-10 lg:grid-cols-2 lg:items-end lg:gap-4">
            <div className="pb-10 lg:self-center lg:pb-0">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
                Dr. Anish Patel
              </h1>
              <p className="mt-3 text-lg md:text-xl font-semibold text-primary">
                Hair Restoration Physician
              </p>
              <p className="mt-6 text-base md:text-lg leading-relaxed text-muted-foreground">
                Dr. Anish Patel is a board-certified physician and hair restoration surgeon
                based in Chicago. Trained extensively by renowned hair restoration surgeon
                Dr. James Harris, Dr. Patel offers a precise, aesthetic approach to hair
                transplantation.
              </p>
            </div>
            <div className="flex justify-center lg:justify-end lg:self-end">
              <img
                src={hero}
                alt="Dr. Anish Patel — Hair Restoration Physician"
                className="w-full max-w-sm object-contain"
                loading="eager"
              />
            </div>
          </div>
        </section>

        {/* Bio / Education tabs */}
        <section className="bg-muted/40 py-12 md:py-16">
          <div className="mx-auto w-full max-w-6xl px-4">
            <div className="grid max-w-3xl grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => setTab("bio")}
                className={`inline-flex items-center justify-center gap-2 px-6 py-5 text-lg font-semibold transition ${
                  tab === "bio"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-foreground shadow-sm hover:bg-card/80"
                }`}
              >
                <User size={20} />
                Full Bio
              </button>
              <button
                type="button"
                onClick={() => setTab("education")}
                className={`inline-flex items-center justify-center gap-2 px-6 py-5 text-lg font-semibold transition ${
                  tab === "education"
                    ? "bg-primary text-primary-foreground"
                    : "bg-card text-foreground shadow-sm hover:bg-card/80"
                }`}
              >
                <GraduationCap size={20} />
                Education
              </button>
            </div>

            <div className="bg-card px-4 py-10 md:px-10 md:py-14 shadow-sm">
              {tab === "bio" ? (
                <div className="mx-auto max-w-6xl">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                      Dr. Anish Patel's Bio
                    </h2>
                    <div className="mt-4 space-y-4 text-base leading-relaxed text-muted-foreground">
                      <p>
                        Dr. Anish Patel is a board-certified physician and hair restoration
                        surgeon based in Chicago. Trained extensively by renowned hair
                        restoration surgeon Dr. James Harris, Dr. Patel offers a precise,
                        aesthetic approach to hair transplantation.
                      </p>
                      <p>
                        An artist at heart, Dr. Patel brings a refined eye for balance and
                        proportion to each procedure. He carefully designs each restoration
                        to create natural-appearing results.
                      </p>
                      <p>
                        His background in Emergency Medicine has given him a strong
                        foundation in anatomy, procedural medicine, and patient care. He
                        combines this experience with his passion for hair restoration to
                        provide thoughtful, individualized care.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mx-auto max-w-6xl">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                    Education &amp; Training
                  </h2>
                  <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                    <div className="rounded-md border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">
                        Bachelor of Arts
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">2011</p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        University of Chicago
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">Chicago, IL</p>
                    </div>
                    <div className="rounded-md border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">
                        Doctor of Medicine (M.D.)
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">2016</p>
                      <p className="mt-2 text-sm text-muted-foreground">Rush Medical College</p>
                      <p className="mt-1 text-sm text-muted-foreground">Chicago, IL</p>
                    </div>
                    <div className="rounded-md border border-border p-6">
                      <p className="text-sm uppercase tracking-wide text-primary">
                        Residency
                      </p>
                      <p className="mt-2 text-lg font-semibold text-foreground">2019</p>
                      <p className="mt-2 text-sm text-muted-foreground">
                        Resurrection Emergency Medicine
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">Chicago, IL</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
        <Suspense fallback={null}>
          <Results resultsHref="https://restorehair.com/results/" resultsButtonLabel="See More Results" />
          <Reviews />
          <SocialPosts />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}
